import { useCallback, useEffect, useEffectEvent, useRef, useState, type ReactElement } from 'react';
import FocusTrap from '../../../Shared/FocusTrap';
import Spinner from '../../../Shared/Spinner';
import { useTexts } from '../../../../hooks/texts.hook';
import LogService from '../../../../services/log-service/log.service';
import DeviceHelper, { DeviceType } from '../../../../helpers/device.helper';
import DataUrlHelper from '../../../../helpers/data-url.helper';
import { SignFault, SignFaultHelper } from '../../../../models/sign-fault.enum';
import { SignCollectStatus } from '../../../../models/sign-collect-status.enum';
import bankidLogoIcon from '../../../../../assets/icons/external/bankid-logo-darkblue.svg';
import CountDownBar from './components/CountDownBar';
import { bankIdRedirectHash, getBankIdLaunchUrl } from './helpers/bankid-launch.helper';
import Grp3SignService from './services/grp3-sign.service';

enum SignStep {
  initial = 'initial',
  collect = 'collect',
  reSign = 'reSign',
  done = 'done',
  error = 'error',
  expired = 'expired',
  cancel = 'cancel',
}

enum ActiveDevice {
  same = 'same',
  other = 'other',
}

interface ISignCollectResponse {
  status: SignCollectStatus;
  fault?: SignFault;
}

interface IGrp3SignProps {
  show: boolean;
  transactionId: string;
  autoStartToken: string;
  onRenew: (transactionId: string) => Promise<string>;
  onComplete: (success: boolean, transactionId: string | null) => void;
}

// === CONFIGURATION ===
const renewalIntervalMs = 25000;
const initialCountdownSeconds = 300;
function isMobileDevice(): boolean {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const nav = window.navigator as any;
  const userAgentDataIsMobile = nav.userAgentData?.mobile;
  return typeof userAgentDataIsMobile === 'boolean' ? userAgentDataIsMobile : typeof window.orientation !== 'undefined' || navigator.userAgent.indexOf('IEMobile') !== -1;
}

function Grp3Sign({ show, transactionId, autoStartToken, onRenew, onComplete }: IGrp3SignProps) {
  // === STATE ===
  const [signStep, setSignStep] = useState(SignStep.initial);
  const [statusText, setStatusText] = useState('');
  const [selectedDevice, setSelectedDevice] = useState(isMobileDevice() ? ActiveDevice.same : ActiveDevice.other);
  const [collectStatus, setCollectStatus] = useState<SignCollectStatus>();
  const [qrCode, setQrCode] = useState<string>();
  const [activeAutoStartToken, setActiveAutoStartToken] = useState(autoStartToken);
  const [countdownSecondsLeft, setCountdownSecondsLeft] = useState(initialCountdownSeconds);
  const [countdownDeadlineMs, setCountdownDeadlineMs] = useState(() => Date.now() + initialCountdownSeconds * 1000);
  const [renewalDeadlineMs, setRenewalDeadlineMs] = useState(() => Date.now() + renewalIntervalMs);
  const [showFullscreenQr, setShowFullscreenQr] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // === REFS ===
  const headingRef = useRef<HTMLHeadingElement>(null);
  const statusRef = useRef<HTMLDivElement>(null);
  const qrButtonRef = useRef<HTMLButtonElement>(null);
  const openBankIdButtonRef = useRef<HTMLButtonElement>(null);
  const fullscreenQrButtonRef = useRef<HTMLButtonElement>(null);
  const fullscreenCloseButtonRef = useRef<HTMLButtonElement>(null);
  const extendButtonRef = useRef<HTMLButtonElement>(null);
  const deviceButtonRef = useRef<HTMLButtonElement>(null);
  const signStepRef = useRef(signStep);
  const selectedDeviceRef = useRef(selectedDevice);
  const collectStatusRef = useRef<SignCollectStatus | undefined>(undefined);
  const qrCodeRef = useRef<string | undefined>(undefined);
  const countdownSecondsLeftRef = useRef(countdownSecondsLeft);
  const qrFailureCountRef = useRef(0);
  const collectFailureCountRef = useRef(0);
  const renewalInProgressRef = useRef(false);
  const signingFinishedRef = useRef(false);
  const transactionClosedRef = useRef(false);
  const completionNotifiedRef = useRef(false);
  const focusQrAfterRenewRef = useRef(false);
  const focusStatusAfterTransitionRef = useRef(false);

  // === VARIABLES ===
  const { t } = useTexts();
  const isSigningActive = signStep === SignStep.initial || signStep === SignStep.collect || signStep === SignStep.reSign;
  const isSigningFinished = signStep === SignStep.done || signStep === SignStep.error || signStep === SignStep.expired || signStep === SignStep.cancel;
  const isRenewing = signStep === SignStep.reSign;
  const canChooseDevice =
    isSigningActive && (signStep === SignStep.initial || signStep === SignStep.reSign || collectStatus === SignCollectStatus.outstandingtransaction || collectStatus === SignCollectStatus.noclient);

  signStepRef.current = signStep;
  selectedDeviceRef.current = selectedDevice;
  countdownSecondsLeftRef.current = countdownSecondsLeft;

  // === HANDLERS ===
  function setStatusMessage(message: string): void {
    setStatusText(message);
  }

  function clearQrCode(): void {
    if (qrCodeRef.current) URL.revokeObjectURL(qrCodeRef.current);
    qrCodeRef.current = undefined;
    setQrCode(undefined);
  }

  const handleFault = useCallback(
    (fault?: SignFault) => {
      signingFinishedRef.current = true;
      clearQrCode();
      setShowFullscreenQr(false);

      switch (fault) {
        case SignFault.expiredtransaction:
          // RFA8
          setStatusMessage(
            t(
              'BankID-appen svarar inte. Kontrollera att den är startad och att du har internetanslutning. Försök sedan igen.',
              'The BankID app is not responding. Please check that it’s started and that you have internet access. Try again. ',
            ),
          );
          setSignStep(SignStep.error);
          break;
        case SignFault.certificateerror:
          // RFA16
          setStatusMessage(
            t(
              'Ditt BankID är för gammalt eller spärrat. Använd ett annat BankID eller skaffa ett nytt hos din bank. ',
              'Your BankID is blocked or too old. Please use another BankID or get a new one from your bank.',
            ),
          );
          setSignStep(SignStep.error);
          break;
        case SignFault.usercancel:
          // RFA6
          setStatusMessage(t('Åtgärden avbröts.', 'The action was cancelled.'));
          setSignStep(SignStep.cancel);
          transactionClosedRef.current = true;
          break;
        case SignFault.cancelled:
          // RFA3
          setStatusMessage(t('Åtgärden avbröts. Försök igen.', 'The action was cancelled. Please try again.'));
          setSignStep(SignStep.cancel);
          transactionClosedRef.current = true;
          break;
        case SignFault.startfailed:
          // RFA17
          setStatusMessage(
            selectedDevice === ActiveDevice.same
              ? t(
                  'Du verkar inte ha BankID-appen/programmet. Installera den och skaffa ett BankID hos din bank.',
                  "You don't seem to have the BankID app/program. Please install it and get a BankID from your bank.",
                )
              : t('Misslyckades att läsa av QR-koden. Starta BankID-appen och läs av QR-koden.', 'Failed to scan the QR code. Start the BankID app and scan the QR code.'),
          );
          setSignStep(SignStep.error);
          break;
        case SignFault.alreadyinprogress:
          // RFA4
          setStatusMessage(
            t(
              'En identifiering eller underskrift pågår redan för ditt personnummer. Försök igen.',
              'An identification or signature is already in progress for your personal identity number. Please try again.',
            ),
          );
          setSignStep(SignStep.error);
          break;
        default:
          // RFA22
          setStatusMessage(t('Något gick fel. Försök igen.', 'Something went wrong. Please try again.'));
          setSignStep(SignStep.error);
          break;
      }
    },
    [t, selectedDevice],
  );

  // https://developers.bankid.com/how-to-guides/autostart#launching-from-a-browser
  const autostartBankId = useCallback(
    (token = activeAutoStartToken) => {
      const launchUrl = getBankIdLaunchUrl(token, DeviceHelper.getBrowser(), window.location.href);
      if (DeviceHelper.getBrowser().deviceType === DeviceType.Desktop) {
        window.location.href = launchUrl;
      } else {
        const link = document.createElement('a');
        link.href = launchUrl;
        link.referrerPolicy = 'origin';
        link.click();
      }
    },
    [activeAutoStartToken],
  );

  function focusQrAction(fullscreen: boolean): void {
    window.setTimeout(() => (countdownSecondsLeftRef.current <= 30 ? extendButtonRef.current : fullscreen ? fullscreenQrButtonRef.current : qrButtonRef.current)?.focus());
  }

  const handleSignCollectResponse = useEffectEvent((response: ISignCollectResponse): void => {
    if (response.status === SignCollectStatus.complete) {
      collectStatusRef.current = response.status;
      setCollectStatus(response.status);
      signingFinishedRef.current = true;
      completionNotifiedRef.current = true;
      setSignStep(SignStep.done);
      setStatusMessage(t('Signeringen är klar. Ansökan skickas.', 'Signing is complete. Submitting the application.'));
      onComplete(true, transactionId);
    } else if (response.status === SignCollectStatus.failed) {
      collectStatusRef.current = response.status;
      setCollectStatus(response.status);
      handleFault(response.fault);
    } else if (response.status === SignCollectStatus.cancelled) {
      setSignStep(SignStep.cancel);
      transactionClosedRef.current = true;
      // RFA3
      setStatusMessage(t('Åtgärden avbröts. Försök igen.', 'The action was cancelled. Please try again.'));
    } else if (response.status === SignCollectStatus.started || response.status === SignCollectStatus.usersign || response.status === SignCollectStatus.noclient) {
      const currentStatus = collectStatusRef.current;
      if (currentStatus === SignCollectStatus.usersign || (currentStatus === SignCollectStatus.started && response.status === SignCollectStatus.noclient)) return;
      const activeElement = document.activeElement;
      focusStatusAfterTransitionRef.current =
        response.status !== SignCollectStatus.noclient &&
        (activeElement === qrButtonRef.current || activeElement === openBankIdButtonRef.current || activeElement === fullscreenCloseButtonRef.current || activeElement === deviceButtonRef.current);
      collectStatusRef.current = response.status;
      setCollectStatus(response.status);
      if (response.status === SignCollectStatus.noclient) {
        // RFA1
        setStatusMessage(t('Starta BankID-appen.', 'Start your BankID app.'));
      } else if (response.status === SignCollectStatus.started) {
        // RFA15
        setStatusMessage(
          isMobileDevice()
            ? t('Söker efter BankID. Säkerställ att du har ett giltigt BankID på den här enheten.', 'Searching for BankID. Make sure you have a valid BankID on this device.')
            : t(
                'Söker efter BankID. Säkerställ att du har ett giltigt BankID på den här datorn. Om du har ett BankID på kort, sätt in kortet i kortläsaren.',
                'Searching for BankID. Make sure you have a valid BankID on this computer. If you have a BankID on card, please insert the card into your card reader.',
              ),
        );
      } else if (response.status === SignCollectStatus.usersign) {
        // RFA9
        setStatusMessage(t('Skriv in din säkerhetskod i BankID-appen och välj Identifiera eller Skriv under.', 'Enter your security code in the BankID app and select Identify or Sign'));
      }
      setSignStep(SignStep.collect);
    } else if (response.status === SignCollectStatus.outstandingtransaction && !collectStatusRef.current) {
      collectStatusRef.current = response.status;
      setCollectStatus(response.status);
      // RFA1
      setStatusMessage(t('Starta BankID-appen.', 'Start your BankID app.'));
    } else if (response.status === SignCollectStatus.processing && collectStatusRef.current !== response.status) {
      // RFA21
      collectStatusRef.current = response.status;
      setCollectStatus(response.status);
      setStatusMessage(t('En underskrift pågår.', 'A signing is in progress.'));
    }
  });

  const renewSigning = useCallback(
    async (focusStatus = true, resetCountdown = true) => {
      if (renewalInProgressRef.current || signingFinishedRef.current || transactionClosedRef.current) return;
      renewalInProgressRef.current = true;
      try {
        setSignStep(SignStep.reSign);
        setStatusMessage(selectedDevice === ActiveDevice.same ? t('BankID-signeringen förnyas.', 'Renewing the BankID signing.') : t('Starta BankID för att signera.', 'Open BankID to sign.'));
        if (focusStatus) window.setTimeout(() => statusRef.current?.focus());
        const renewedAutoStartToken = await onRenew(transactionId);
        if (signingFinishedRef.current) {
          void Grp3SignService.cancel(transactionId).catch(() => undefined);
          return;
        }
        setActiveAutoStartToken(renewedAutoStartToken);
        collectStatusRef.current = undefined;
        setCollectStatus(undefined);
        if (resetCountdown) {
          setCountdownSecondsLeft(initialCountdownSeconds);
          setCountdownDeadlineMs(Date.now() + initialCountdownSeconds * 1000);
        }
        qrFailureCountRef.current = 0;
        collectFailureCountRef.current = 0;
        focusQrAfterRenewRef.current = focusStatus && selectedDevice === ActiveDevice.other;
        setRenewalDeadlineMs(Date.now() + renewalIntervalMs);
        setSignStep(SignStep.initial);
      } catch (error) {
        LogService.logError(error, 'Failed to renew GRP3 BankID signing');
        handleFault();
      } finally {
        renewalInProgressRef.current = false;
      }
    },
    [selectedDevice, handleFault, onRenew, t, transactionId],
  );

  // === EFFECTS ===
  useEffect(() => {
    if (show) headingRef.current?.focus();
  }, [show]);

  useEffect(() => {
    if (window.location.hash === `#${bankIdRedirectHash}`) window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
  }, []);

  useEffect(() => {
    if (!show || isSigningFinished) return;

    function updateCountdown(): void {
      const timeLeft = Math.max(0, Math.ceil((countdownDeadlineMs - Date.now()) / 1000));
      setCountdownSecondsLeft(timeLeft);
      if (timeLeft === 0) {
        signingFinishedRef.current = true;
        setSignStep(SignStep.expired);
      }
    }

    updateCountdown();
    const timer = window.setInterval(updateCountdown, 1000);
    document.addEventListener('visibilitychange', updateCountdown);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', updateCountdown);
    };
  }, [countdownDeadlineMs, isSigningFinished, show]);

  useEffect(() => {
    if (!show || (signStep !== SignStep.error && signStep !== SignStep.expired) || transactionClosedRef.current) return;
    if (signStep === SignStep.expired) setStatusMessage(t('Tiden för signeringen gick ut. Stäng och starta en ny signering.', 'The signing expired. Close and start a new signing.'));
    clearQrCode();
    setShowFullscreenQr(false);
    if (focusStatusAfterTransitionRef.current) window.setTimeout(() => statusRef.current?.focus());
    transactionClosedRef.current = true;
    signingFinishedRef.current = true;
    void Grp3SignService.cancel(transactionId).catch(() => {
      transactionClosedRef.current = false;
    });
  }, [signStep, show, t, transactionId]);

  useEffect(() => {
    if (
      !show ||
      selectedDevice === ActiveDevice.same ||
      (collectStatus !== undefined && collectStatus !== SignCollectStatus.outstandingtransaction && collectStatus !== SignCollectStatus.noclient) ||
      (signStep !== SignStep.initial && signStep !== SignStep.collect)
    )
      return;
    let active = true;
    const abortController = new AbortController();

    async function updateQrCode() {
      try {
        const nextQrCode = URL.createObjectURL(await Grp3SignService.getQrImage(transactionId, abortController.signal));
        qrFailureCountRef.current = 0;
        if (!active) return URL.revokeObjectURL(nextQrCode);
        if (qrCodeRef.current) URL.revokeObjectURL(qrCodeRef.current);
        qrCodeRef.current = nextQrCode;
        setQrCode(nextQrCode);
      } catch (error) {
        if (!abortController.signal.aborted && ++qrFailureCountRef.current > 1) {
          LogService.logError(error, 'Failed to refresh the GRP3 BankID QR code');
          handleFault();
        }
      }
    }

    let timeout = window.setTimeout(async function pollQrCode() {
      await updateQrCode();
      if (active) timeout = window.setTimeout(pollQrCode, 1000);
    });
    return () => {
      active = false;
      abortController.abort();
      window.clearTimeout(timeout);
    };
  }, [selectedDevice, collectStatus, signStep, handleFault, show, transactionId]);

  useEffect(() => {
    if (!show || signStep === SignStep.reSign || isSigningFinished) return;
    let active = true;
    const abortController = new AbortController();

    async function collect() {
      try {
        const result = await Grp3SignService.collect(transactionId, abortController.signal);
        collectFailureCountRef.current = 0;
        if (active && !signingFinishedRef.current) handleSignCollectResponse(result);
      } catch (error) {
        if (abortController.signal.aborted) return;
        const fault = error instanceof Error ? SignFaultHelper.parse(error.message) : SignFault.unknown;
        if (fault !== SignFault.unknown) {
          handleFault(fault);
        } else if (++collectFailureCountRef.current > 1) {
          LogService.logError(error, 'Failed to collect GRP3 BankID signing status');
          handleFault();
        }
      }
    }

    let timeout = window.setTimeout(async function pollStatus() {
      await collect();
      if (active) timeout = window.setTimeout(pollStatus, 2000);
    });
    return () => {
      active = false;
      abortController.abort();
      window.clearTimeout(timeout);
    };
  }, [signStep, handleFault, isSigningFinished, onComplete, show, transactionId]);

  useEffect(() => {
    if (!show || (signStep !== SignStep.initial && collectStatus !== SignCollectStatus.noclient)) return;
    const timeout = window.setTimeout(
      () => {
        if (countdownSecondsLeftRef.current > 5 && (signStepRef.current === SignStep.initial || collectStatus === SignCollectStatus.noclient)) void renewSigning(false, false);
      },
      Math.max(0, renewalDeadlineMs - Date.now()),
    );
    return () => window.clearTimeout(timeout);
  }, [collectStatus, signStep, renewSigning, renewalDeadlineMs, show]);

  useEffect(() => {
    if (!qrCode || !focusQrAfterRenewRef.current) return;
    focusQrAfterRenewRef.current = false;
    focusQrAction(showFullscreenQr);
  }, [qrCode, showFullscreenQr]);

  useEffect(() => {
    if (collectStatus !== SignCollectStatus.started && collectStatus !== SignCollectStatus.usersign) return;
    clearQrCode();
    setShowFullscreenQr(false);
    if (focusStatusAfterTransitionRef.current) window.setTimeout(() => statusRef.current?.focus());
  }, [collectStatus]);

  useEffect(() => {
    function cancelOnPageHide() {
      if (selectedDeviceRef.current === ActiveDevice.other && !transactionClosedRef.current && !completionNotifiedRef.current) void Grp3SignService.cancel(transactionId).catch(() => undefined);
    }
    window.addEventListener('pagehide', cancelOnPageHide);
    return () => {
      window.removeEventListener('pagehide', cancelOnPageHide);
      if (qrCodeRef.current) URL.revokeObjectURL(qrCodeRef.current);
    };
  }, [transactionId]);

  useEffect(() => {
    function handleFullscreenChange(): void {
      if (!document.fullscreenElement) {
        setShowFullscreenQr(false);
        focusQrAction(false);
      }
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (showFullscreenQr && !document.fullscreenElement && container.requestFullscreen) {
      void container.requestFullscreen().catch((error) => {
        LogService.logError(error, 'Failed to enter fullscreen');
        setShowFullscreenQr(false);
      });
    } else if (!showFullscreenQr && document.fullscreenElement === container) {
      void document.exitFullscreen().catch((error) => {
        LogService.logError(error, 'Failed to exit fullscreen');
      });
    }
  }, [showFullscreenQr]);

  useEffect(() => {
    if (show && selectedDevice === ActiveDevice.same && openBankIdButtonRef.current) {
      openBankIdButtonRef.current.focus();
    }
  }, [show, selectedDevice]);

  // === HANDLERS ===
  async function cancelSigningIfNeeded(): Promise<void> {
    if (completionNotifiedRef.current) return;
    completionNotifiedRef.current = true;
    signingFinishedRef.current = true;
    setSignStep(SignStep.cancel);
    if (!transactionClosedRef.current) {
      transactionClosedRef.current = true;
      void Grp3SignService.cancel(transactionId).catch(() => undefined);
    }
  }

  function reset(): void {
    clearQrCode();
    setSelectedDevice(isMobileDevice() ? ActiveDevice.same : ActiveDevice.other);
    setStatusText('');
    collectStatusRef.current = undefined;
    setCollectStatus(undefined);
    setActiveAutoStartToken(autoStartToken);
    setCountdownSecondsLeft(initialCountdownSeconds);
    setCountdownDeadlineMs(Date.now() + initialCountdownSeconds * 1000);
    setRenewalDeadlineMs(Date.now() + renewalIntervalMs);
    setShowFullscreenQr(false);
  }

  function handleClose(): void {
    void cancelSigningIfNeeded();
    reset();
    onComplete(false, null);
  }

  function handleDeviceChange(): void {
    if (selectedDevice === ActiveDevice.other) {
      clearQrCode();
      selectedDeviceRef.current = ActiveDevice.same;
      setSelectedDevice(ActiveDevice.same);
    } else {
      selectedDeviceRef.current = ActiveDevice.other;
      setSelectedDevice(ActiveDevice.other);
    }
  }

  function instructionsOtherDevice(): ReactElement {
    return (
      <div className="d-flex justify-content-center">
        <ol>
          <li className="text-start">{t('Öppna BankID på den andra enheten.', 'Open BankID on the other device.')}</li>
          <li className="text-start">{t('Välj Skanna QR-kod.', 'Select Scan QR code.')}</li>
          <li className="text-start">{t('Skanna QR-koden.', 'Scan the QR code.')}</li>
          <li className="text-start">{t('Följ instruktionerna och signera i BankID.', 'Follow the instructions and sign in BankID.')}</li>
        </ol>
      </div>
    );
  }

  if (!show) return null;

  const countdownBar = (
    <>
      <CountDownBar
        timeLeft={countdownSecondsLeft}
        initialTimeLeft={initialCountdownSeconds}
        className="mb-2"
        extendSign={() => void renewSigning()}
        extendButtonDisabled={isRenewing}
        extendButtonRef={extendButtonRef}
      />
    </>
  );

  return (
    <div ref={containerRef}>
      <FocusTrap
        key={showFullscreenQr ? 'fullscreen' : 'default'}
        isVisible={show}
        onEscape={() => {
          if (showFullscreenQr) {
            setShowFullscreenQr(false);
            focusQrAction(false);
          } else handleClose();
        }}
      >
        <div className="modal d-block fade show" aria-labelledby="grp3-sign-title" aria-modal="true" role="dialog">
          <div className={`modal-dialog ${showFullscreenQr ? 'modal-fullscreen' : ''}`}>
            <div className="modal-content">
              <div className="modal-header">
                <h2 id="grp3-sign-title" className="modal-title h5" tabIndex={-1} ref={headingRef}>
                  {t('Signera med BankID', 'Sign with BankID')}
                </h2>
                {!showFullscreenQr && <button type="button" className="btn-close" aria-label={t('Stäng', 'Close')} onClick={handleClose}></button>}
              </div>
              {showFullscreenQr ? (
                <div className="modal-body py-3 text-center">
                  <div className="d-flex align-items-center justify-content-center mx-auto" style={{ minHeight: 256 }}>
                    {qrCode ? (
                      <div className="position-relative d-inline-block">
                        <button
                          ref={fullscreenQrButtonRef}
                          type="button"
                          className="btn btn--unstyled btn--no-box-shadow"
                          disabled={isRenewing}
                          aria-label={t('Stäng helskärm', 'Close fullscreen')}
                          onClick={() => {
                            setShowFullscreenQr(false);
                            focusQrAction(false);
                          }}
                        >
                          <img
                            className={`mw-100 ${isRenewing ? 'opacity-25' : ''}`}
                            style={isRenewing ? { filter: 'blur(20px)' } : undefined}
                            src={qrCode}
                            alt={t('Läs av den här QR-koden', 'Scan this QR code')}
                          />
                        </button>
                        {isRenewing && <Spinner className="position-absolute top-0 start-0 w-100 h-100" text={t('QR-koden uppdateras', 'Updating the QR code')} />}
                      </div>
                    ) : (
                      <Spinner text={t('QR-koden uppdateras', 'Updating the QR code')} />
                    )}
                  </div>
                  {isSigningActive && countdownBar}
                  <button
                    ref={fullscreenCloseButtonRef}
                    type="button"
                    className="btn btn-secondary d-block mx-auto mt-3"
                    onClick={() => {
                      setShowFullscreenQr(false);
                      focusQrAction(false);
                    }}
                  >
                    {t('Stäng helskärm', 'Close fullscreen')}
                  </button>
                </div>
              ) : (
                <>
                  <div className="modal-body py-3 text-center">
                    {isSigningActive && (selectedDevice === ActiveDevice.same || !qrCode) && (
                      <img src={DataUrlHelper.encode(bankidLogoIcon)} alt={t('BankID-logotyp', 'BankID logo')} className="d-inline-block w-50" />
                    )}
                    {selectedDevice === ActiveDevice.other && canChooseDevice && qrCode && (
                      <button
                        ref={qrButtonRef}
                        type="button"
                        className="btn btn--unstyled btn--no-box-shadow position-relative"
                        disabled={isRenewing}
                        aria-label={t('Öppna helskärm', 'Open fullscreen')}
                        onClick={() => {
                          setShowFullscreenQr(true);
                          focusQrAction(true);
                        }}
                      >
                        <img
                          src={qrCode}
                          alt={t('Läs av den här QR-koden', 'Scan this QR code')}
                          className={`mb-2 ${isRenewing ? 'opacity-25' : ''}`}
                          style={isRenewing ? { filter: 'blur(20px)' } : undefined}
                        />
                        {isRenewing && <Spinner className="position-absolute top-0 start-0 w-100 h-100" text={t('QR-koden uppdateras', 'Updating the QR code')} />}
                      </button>
                    )}
                    {selectedDevice === ActiveDevice.other && canChooseDevice && !qrCode && (
                      <Spinner className="mb-3" text={isRenewing ? t('QR-koden uppdateras', 'Updating the QR code') : t('QR-koden hämtas', 'Loading the QR code')} />
                    )}
                    {isSigningActive && countdownBar}
                    <div ref={statusRef} role={signStep === SignStep.error || signStep === SignStep.expired ? 'alert' : 'status'} className={isSigningActive ? 'mb-3' : ''} tabIndex={-1}>
                      {statusText}
                    </div>
                    {selectedDevice === ActiveDevice.other && canChooseDevice && qrCode && instructionsOtherDevice()}
                    {canChooseDevice && (
                      <div className="w-100">
                        {selectedDevice === ActiveDevice.same && (
                          <div className="w-100 mb-3 pb-3 border-bottom">
                            <button ref={openBankIdButtonRef} type="button" className="btn btn-primary" onClick={() => autostartBankId()} disabled={isRenewing}>
                              <span>{t('Starta BankID-appen', 'Start the BankID app')}</span>
                            </button>
                          </div>
                        )}
                        <button ref={deviceButtonRef} type="button" className="btn btn-tertiary" disabled={isRenewing} onClick={handleDeviceChange}>
                          {selectedDevice === ActiveDevice.same ? t('BankID på annan enhet', 'BankID on another device') : t('BankID på den här enheten', 'BankID on this device')}
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="btn btn-secondary w-100" onClick={handleClose}>
                      {t('Avbryt', 'Cancel')}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </FocusTrap>
      <div className="modal-backdrop fade show"></div>
    </div>
  );
}

export default Grp3Sign;

